import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BusinessFlags {
  allow_dvla?: boolean | number;
  allow_mobile_dvla_checks?: boolean | number;
  allow_dvla_management_mobile?: boolean | number;
  hide_templates?: boolean | number;
  allow_safety_checks?: boolean | number;
  allow_loan_checks?: boolean | number;
  allow_delivery_checks?: boolean | number;
  allow_location_checks?: boolean | number;
  use_site_terminology?: boolean | number;
  allow_incidents?: boolean | number;
  allow_hr_module?: boolean | number;
  allow_calendar?: boolean | number;
  allow_assets_management?: boolean | number;
  allow_installations?: boolean | number;
}

export interface UserModuleRestrictions {
  // Login access (Super Admin / Owner only)
  web_login?: boolean;
  app_login?: boolean;
  // Module access
  dvla_web?: boolean;
  dvla_mobile?: boolean;
  templates?: boolean;
  safety_checks?: boolean;
  loan_checks?: boolean;
  delivery_checks?: boolean;
  location_checks?: boolean;
  incidents?: boolean;
  assets?: boolean;
  reports?: boolean;
  hr?: boolean;
  calendar?: boolean;
  installations?: boolean;
  communications?: boolean;
  locations?: boolean;
  // Incident sub-permissions
  incidents_own_only?: boolean;
  incidents_view_only?: boolean;
  incidents_no_create?: boolean;
  incidents_no_close?: boolean;
  incidents_no_remedial?: boolean;
  // Check sub-permissions
  checks_view_only?: boolean;
  checks_no_create?: boolean;
  checks_no_delete?: boolean;
  checks_no_remedial?: boolean;
  location_checks_no_remedial?: boolean;
}

interface ScopeOption { id: number; label: string; }

interface UserScope {
  allowedLocations: ScopeOption[];
  allowedAssetTypes: ScopeOption[];
  allowedAssets: ScopeOption[];
}

// ── Restriction config ────────────────────────────────────────────────────────

interface RestrictableModule {
  key: keyof UserModuleRestrictions;
  label: string;
  description: string;
  available: (flags: BusinessFlags, canEditLogin: boolean) => boolean;
  warning?: string;
}

const ACCESS_RESTRICTIONS: RestrictableModule[] = [
  {
    key: 'web_login',
    label: 'Block Web Login',
    description: 'Prevent this user from logging into the web admin portal entirely.',
    available: (_f, canEditLogin) => canEditLogin,
    warning: 'This will immediately prevent the user from accessing the web portal.',
  },
  {
    key: 'app_login',
    label: 'Block App Login',
    description: 'Prevent this user from logging into the mobile app entirely.',
    available: (_f, canEditLogin) => canEditLogin,
    warning: 'This will immediately prevent the user from accessing the mobile app.',
  },
];

const MODULE_RESTRICTIONS: RestrictableModule[] = [
  {
    key: 'safety_checks',
    label: 'Safety Checks',
    description: 'Restrict access to the Safety Checks module.',
    available: (f) => !!(f.allow_safety_checks === 1 || f.allow_safety_checks === true),
  },
  {
    key: 'loan_checks',
    label: 'Loan Checks',
    description: 'Restrict access to the Loan Checks module.',
    available: (f) => !!(f.allow_loan_checks === 1 || f.allow_loan_checks === true),
  },
  {
    key: 'delivery_checks',
    label: 'Delivery Checks',
    description: 'Restrict access to the Delivery Checks module.',
    available: (f) => !!(f.allow_delivery_checks === 1 || f.allow_delivery_checks === true),
  },
  {
    key: 'location_checks',
    label: 'Location Checks',
    description: 'Restrict access to the Location Checks module.',
    available: (f) => !!(f.allow_location_checks === 1 || f.allow_location_checks === true),
  },
  {
    key: 'incidents',
    label: 'Incidents (full block)',
    description: 'Remove all access to the Incidents module.',
    available: (f) => !!(f.allow_incidents === 1 || f.allow_incidents === true),
  },
  {
    key: 'assets',
    label: 'Assets',
    description: 'Restrict access to the Assets module.',
    available: () => true,
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'Restrict access to the Reports section.',
    available: () => true,
  },
  {
    key: 'templates',
    label: 'Templates',
    description: 'Restrict access to check templates.',
    available: (f) => !(f.hide_templates === 1 || f.hide_templates === true),
  },
  {
    key: 'hr',
    label: 'HR / Shifts',
    description: 'Restrict access to the HR and Shifts module.',
    available: (f) => !!(f.allow_hr_module === 1 || f.allow_hr_module === true),
  },
  {
    key: 'calendar',
    label: 'Calendar',
    description: 'Restrict access to the Calendar and Bookings section.',
    available: (f) => f.allow_calendar !== 0 && f.allow_calendar !== false && f.allow_calendar != null,
  },
  {
    key: 'installations',
    label: 'Installations',
    description: 'Restrict access to the Installations module.',
    available: (f) => !!(f.allow_installations === 1 || f.allow_installations === true),
  },
  {
    key: 'communications',
    label: 'Communications',
    description: 'Restrict access to the Communications section.',
    available: () => true,
  },
  {
    key: 'locations',
    label: 'Locations / Sites',
    description: 'Restrict access to the Locations and Sites module.',
    available: () => true,
  },
  {
    key: 'dvla_web',
    label: 'DVLA (Web)',
    description: 'Restrict access to the DVLA section in the web admin portal.',
    available: (f) => !!(f.allow_dvla === 1 || f.allow_dvla === true),
  },
  {
    key: 'dvla_mobile',
    label: 'DVLA (App)',
    description: 'Restrict access to DVLA checks and management in the mobile app.',
    available: (f) =>
      !!(f.allow_mobile_dvla_checks === 1 || f.allow_mobile_dvla_checks === true) ||
      !!(f.allow_dvla_management_mobile === 1 || f.allow_dvla_management_mobile === true),
  },
];

const ACTION_RESTRICTIONS: RestrictableModule[] = [
  {
    key: 'checks_view_only',
    label: 'Checks: View Only',
    description: 'User can view checks but cannot complete or submit them.',
    available: () => true,
  },
  {
    key: 'checks_no_create',
    label: 'Checks: Cannot Create',
    description: 'User cannot create new checks from any check module.',
    available: () => true,
  },
  {
    key: 'checks_no_delete',
    label: 'Checks: Cannot Delete',
    description: 'User cannot delete check records.',
    available: () => true,
  },
  {
    key: 'checks_no_remedial',
    label: 'Checks: No Remedial Actions',
    description: 'User cannot log or complete remedial actions on failed checks.',
    available: () => true,
  },
  {
    key: 'location_checks_no_remedial',
    label: 'Location Checks: No Remedial Actions',
    description: 'User cannot log or complete remedial actions on location checks specifically.',
    available: (f) => !!(f.allow_location_checks === 1 || f.allow_location_checks === true),
  },
  {
    key: 'incidents_own_only',
    label: 'Incidents: Own Reports Only',
    description: 'User can only see incidents they reported. Admins are unaffected.',
    available: (f) => !!(f.allow_incidents === 1 || f.allow_incidents === true),
  },
  {
    key: 'incidents_view_only',
    label: 'Incidents: View Only',
    description: 'User can view incidents but cannot add updates or change status.',
    available: (f) => !!(f.allow_incidents === 1 || f.allow_incidents === true),
  },
  {
    key: 'incidents_no_create',
    label: 'Incidents: Cannot Create',
    description: 'User cannot log new incidents.',
    available: (f) => !!(f.allow_incidents === 1 || f.allow_incidents === true),
  },
  {
    key: 'incidents_no_close',
    label: 'Incidents: Cannot Close/Resolve',
    description: 'User cannot close or resolve incidents.',
    available: (f) => !!(f.allow_incidents === 1 || f.allow_incidents === true),
  },
  {
    key: 'incidents_no_remedial',
    label: 'Incidents: No Remedial Actions',
    description: 'User cannot add or complete remedial actions on incidents.',
    available: (f) => !!(f.allow_incidents === 1 || f.allow_incidents === true),
  },
];

// ── Props & helpers ──────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  /** Whether the current user has permission to edit restrictions */
  canEdit: boolean;
  /** Whether the current user can set login-blocking keys (Super Admin / Owner only) */
  canEditLogin?: boolean;
}

// ── Toggle row component ──────────────────────────────────────────────────────

interface ToggleRowProps {
  mod: RestrictableModule;
  isRestricted: boolean;
  canEdit: boolean;
  onToggle: (key: keyof UserModuleRestrictions) => void;
}

function ToggleRow({ mod, isRestricted, canEdit, onToggle }: ToggleRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 0.5,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, mr: 2 }}>
        <Box display="flex" alignItems="center" gap={0.75}>
          {isRestricted ? (
            <LockIcon fontSize="small" color="warning" />
          ) : (
            <LockOpenIcon fontSize="small" color="success" />
          )}
          <Typography variant="body2" fontWeight={500}>
            {mod.label}
          </Typography>
          {mod.warning && isRestricted && (
            <Tooltip title={mod.warning}>
              <WarningAmberIcon fontSize="small" color="error" />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {mod.description}
        </Typography>
      </Box>
      <Tooltip
        title={
          !canEdit
            ? 'Only Admins, Super Admins, and Multi Business Managers can change restrictions'
            : isRestricted
            ? 'Click to grant access'
            : 'Click to restrict access'
        }
      >
        <span>
          <FormControlLabel
            control={
              <Switch
                checked={isRestricted}
                onChange={() => canEdit && onToggle(mod.key)}
                color="warning"
                disabled={!canEdit}
                size="small"
              />
            }
            label={
              <Typography
                variant="caption"
                color={isRestricted ? 'warning.main' : 'success.main'}
                fontWeight={600}
              >
                {isRestricted ? 'Restricted' : 'Allowed'}
              </Typography>
            }
            labelPlacement="start"
            sx={{ m: 0 }}
          />
        </span>
      </Tooltip>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const UserRestrictionsDialog: React.FC<Props> = ({
  open,
  onClose,
  userId,
  userName,
  canEdit,
  canEditLogin = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [restrictions, setRestrictions] = useState<UserModuleRestrictions>({});
  const [businessFlags, setBusinessFlags] = useState<BusinessFlags>({});
  const [tab, setTab] = useState(0);

  // Scope state
  const [scope, setScope] = useState<UserScope>({ allowedLocations: [], allowedAssetTypes: [], allowedAssets: [] });
  const [allLocations, setAllLocations] = useState<ScopeOption[]>([]);
  const [allAssetTypes, setAllAssetTypes] = useState<ScopeOption[]>([]);
  const [allAssets, setAllAssets] = useState<ScopeOption[]>([]);
  const [scopeDirty, setScopeDirty] = useState(false);

  const token = localStorage.getItem('token');
  const businessId = localStorage.getItem('businessId');

  const load = useCallback(async () => {
    if (!open || !userId) return;
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    setScopeDirty(false);
    try {
      const requests: Promise<any>[] = [
        axios.get(`${API_BASE_URL}/users/${userId}/restrictions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/users/${userId}/scope`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        businessId
          ? axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : Promise.resolve({ data: {} }),
      ];

      if (businessId) {
        requests.push(
          axios.get(`${API_BASE_URL}/locations?businessId=${businessId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/asset-types`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/assets`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }

      const [restrictRes, scopeRes, bizRes, locRes, atRes, assetRes] = await Promise.all(requests);

      setRestrictions(restrictRes.data?.userModuleRestrictions ?? {});
      setBusinessFlags(bizRes.data ?? {});

      const scopeData = scopeRes.data ?? {};
      setScope({
        allowedLocations: (scopeData.allowedLocations ?? []).map((r: any) => ({
          id: r.location_id ?? r.locationId,
          label: r.location_name ?? r.locationName,
        })),
        allowedAssetTypes: (scopeData.allowedAssetTypes ?? []).map((r: any) => ({
          id: r.asset_type_id,
          label: r.type_name,
        })),
        allowedAssets: (scopeData.allowedAssets ?? []).map((r: any) => ({
          id: r.asset_id,
          label: r.asset_name,
        })),
      });

      if (locRes) {
        // /api/locations returns a plain array; items use camelCase locationId/locationName
        const locs: any[] = Array.isArray(locRes.data) ? locRes.data : (locRes.data?.locations ?? []);
        setAllLocations(locs.map((l: any) => ({ id: l.locationId ?? l.location_id, label: l.locationName ?? l.location_name })));
      }
      if (atRes) {
        // /api/asset-types returns { success, data: [...] }; items use asset_type_id/type_name
        const types: any[] = atRes.data?.data ?? atRes.data?.assetTypes ?? (Array.isArray(atRes.data) ? atRes.data : []);
        setAllAssetTypes(types.map((t: any) => ({ id: t.asset_type_id, label: t.type_name })));
      }
      if (assetRes) {
        // /api/assets returns a plain array; items use asset_id/asset_name
        const assets: any[] = Array.isArray(assetRes.data) ? assetRes.data : (assetRes.data?.assets ?? []);
        setAllAssets(assets.map((a: any) => ({ id: a.asset_id, label: a.asset_name })));
      }
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to load restrictions');
    } finally {
      setLoading(false);
    }
  }, [open, userId, token, businessId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = (key: keyof UserModuleRestrictions) => {
    setRestrictions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await axios.put(
        `${API_BASE_URL}/users/${userId}/restrictions`,
        { userModuleRestrictions: restrictions },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (scopeDirty) {
        await axios.put(
          `${API_BASE_URL}/users/${userId}/scope`,
          {
            allowedLocations: scope.allowedLocations.map(l => l.id),
            allowedAssetTypes: scope.allowedAssetTypes.map(t => t.id),
            allowedAssets: scope.allowedAssets.map(a => a.id),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setSuccessMsg('Restrictions saved successfully');
      setScopeDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to save restrictions');
    } finally {
      setSaving(false);
    }
  };

  // Conflict detection
  const conflicts: string[] = [];
  if (restrictions.incidents && (restrictions.incidents_own_only || restrictions.incidents_view_only || restrictions.incidents_no_create)) {
    conflicts.push('Incidents is fully blocked — the individual incident sub-restrictions below are redundant.');
  }

  // Count totals
  const allMods = [...ACCESS_RESTRICTIONS, ...MODULE_RESTRICTIONS, ...ACTION_RESTRICTIONS];
  const restrictedCount = allMods.filter((m) => restrictions[m.key]).length;
  const hasScopeRestrictions = scope.allowedLocations.length > 0 || scope.allowedAssetTypes.length > 0 || scope.allowedAssets.length > 0;

  const renderModuleList = (modules: RestrictableModule[]) => {
    const available = modules.filter((m) => m.available(businessFlags, canEditLogin));
    if (available.length === 0) {
      return (
        <Typography color="text.secondary" textAlign="center" py={3} variant="body2">
          No applicable restrictions for this business configuration.
        </Typography>
      );
    }
    return (
      <Box>
        {available.map((mod, idx) => (
          <React.Fragment key={mod.key}>
            {idx > 0 && <Divider />}
            <ToggleRow
              mod={mod}
              isRestricted={Boolean(restrictions[mod.key])}
              canEdit={canEdit}
              onToggle={handleToggle}
            />
          </React.Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <LockIcon color="primary" />
          <Box>
            <Typography variant="h6" component="span">
              User Restrictions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userName}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75 }}>
            {restrictedCount > 0 && (
              <Chip label={`${restrictedCount} restricted`} color="warning" size="small" />
            )}
            {hasScopeRestrictions && (
              <Chip label="scope limited" color="info" size="small" />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {canEditLogin && <Tab label="Access" />}
          <Tab label="Modules" />
          <Tab label="Actions" />
          <Tab label="Data Scope" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
            {conflicts.length > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                {conflicts.map((c, i) => <div key={i}>{c}</div>)}
              </Alert>
            )}

            {/* Access tab — only rendered when canEditLogin */}
            {canEditLogin && tab === 0 && (
              <>
                <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                  <strong>Login blocking</strong> — these settings immediately prevent the user from signing in to the
                  selected platform. Only Super Admins and Owners can change these.
                </Alert>
                {renderModuleList(ACCESS_RESTRICTIONS)}
              </>
            )}

            {/* Modules tab */}
            {tab === (canEditLogin ? 1 : 0) && (
              <>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                  Toggle a module <strong>on</strong> to block this user from accessing it. Only modules enabled
                  for your business are shown.
                </Alert>
                {renderModuleList(MODULE_RESTRICTIONS)}
              </>
            )}

            {/* Actions tab */}
            {tab === (canEditLogin ? 2 : 1) && (
              <>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                  Fine-grained action restrictions — restrict specific operations within modules without
                  blocking the entire module. Server-enforced.
                </Alert>
                {renderModuleList(ACTION_RESTRICTIONS)}
              </>
            )}

            {/* Data Scope tab */}
            {tab === (canEditLogin ? 3 : 2) && (
              <Box>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                  Limit which locations and assets this user can see. Leave empty to allow access to all.
                  Scoping is server-enforced — the user will only receive data for items in their whitelist.
                </Alert>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  Allowed Locations
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Empty = all locations. Add specific locations to restrict visibility.
                </Typography>
                <Autocomplete
                  multiple
                  options={allLocations}
                  value={scope.allowedLocations}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={!canEdit}
                  onChange={(_, val) => { setScope(s => ({ ...s, allowedLocations: val })); setScopeDirty(true); }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="All locations (unrestricted)" size="small" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => (
                      <Chip {...getTagProps({ index: idx })} key={opt.id} label={opt.label} size="small" />
                    ))
                  }
                  sx={{ mb: 2.5 }}
                />

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Allowed Asset Types
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Empty = all asset types. Use to restrict by category (e.g. "vehicles only").
                </Typography>
                <Autocomplete
                  multiple
                  options={allAssetTypes}
                  value={scope.allowedAssetTypes}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={!canEdit}
                  onChange={(_, val) => { setScope(s => ({ ...s, allowedAssetTypes: val })); setScopeDirty(true); }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="All asset types (unrestricted)" size="small" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => (
                      <Chip {...getTagProps({ index: idx })} key={opt.id} label={opt.label} size="small" />
                    ))
                  }
                  sx={{ mb: 2.5 }}
                />

                <Typography variant="subtitle2" gutterBottom>
                  Allowed Specific Assets
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Empty = all assets (or combined with asset type filter above).
                </Typography>
                <Autocomplete
                  multiple
                  options={allAssets}
                  value={scope.allowedAssets}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={!canEdit}
                  onChange={(_, val) => { setScope(s => ({ ...s, allowedAssets: val })); setScopeDirty(true); }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="All assets (unrestricted)" size="small" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => (
                      <Chip {...getTagProps({ index: idx })} key={opt.id} label={opt.label} size="small" />
                    ))
                  }
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          {canEdit ? 'Cancel' : 'Close'}
        </Button>
        {canEdit && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} /> : <LockIcon />}
          >
            {saving ? 'Saving…' : 'Save Restrictions'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserRestrictionsDialog;
